import { notification } from "../context/NotificationsContext";
import { reportTelemetryError } from "../telemetry";
import { captureScreenshot } from "../telemetry/telemetry";
import { dmnIdentity } from "../utils/dmnIdentity";
import { getEnvironmentProps, isLocalDev } from "../utils/environment";
import { consoleLog } from "../utils/nativeUtils/customActions";

interface ErrorResponse {
  error?: string;
  message?: string;
  payload?: {
    message?: string;
  };
}

function isErrorResponse(obj: unknown): obj is ErrorResponse {
  return typeof obj === "object" && obj !== null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message: string,
    public responseError?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Throws an ApiError after attempting to extract error details from the response.
 * Also calls apiFetchNotOkHandler to display error notifications to the user.
 *
 * @param response - The failed HTTP response object
 * @param errorTitle - A title/context for the error (e.g., "Error fetching organisations")
 * @returns Never returns - always throws an ApiError
 * @throws {ApiError} Contains HTTP status, status text, and extracted error message
 */
export const throwApiError = async (
  response: Response,
  errorTitle: string
): Promise<never> => {
  let responseError: unknown = null;
  let apiErrorMessage = "";

  try {
    responseError = await response.clone().json();

    if (isErrorResponse(responseError)) {
      if (responseError.error) {
        apiErrorMessage = responseError.error;
      }

      if (responseError.message) {
        apiErrorMessage = responseError.message;
      }

      if (responseError.payload?.message) {
        apiErrorMessage = responseError.payload.message;
      }
    }
  } catch {
    // Ignore JSON parsing errors - continue without API error message
  }

  await apiFetchNotOkHandler(response, errorTitle);

  const statusMessage = `HTTP ${response.status}: ${response.statusText}`;
  const fullMessage = apiErrorMessage
    ? `${statusMessage} - ${apiErrorMessage}`
    : statusMessage;

  throw new ApiError(
    response.status,
    response.statusText,
    fullMessage,
    responseError
  );
};

const getCurrentEnvironment = async (): Promise<string> => {
  try {
    const token = await dmnIdentity.getAccessToken();
    const envProps = getEnvironmentProps(token);
    return envProps?.ENVIRONMENT ?? "unknown";
  } catch (error) {
    console.warn("Could not determine environment for error reporting:", error);
    return isLocalDev() ? "development" : "unknown";
  }
};

export const apiFetchNotOkHandler = async (
  response: Response,
  errorTitle: string
) => {
  let apiError = "";
  let error: unknown = {};
  try {
    error = await response?.json?.();
  } catch (e) {
    error = e;
  }

  if (isErrorResponse(error)) {
    if (error.error) {
      apiError = ` ${error.error}`;
    }

    if (error.message) {
      apiError = ` ${error.message}`;
    }

    if (error.payload?.message) {
      apiError = ` ${error.payload.message}`;
    }
  }

  let message = "";
  switch (response.status) {
    case 400:
      message = "Bad Request";
      break;
    case 401:
      message = "Unauthorised";
      break;
    case 403:
      message = "Forbidden";
      break;
    case 404:
      message = "Not Found";
      break;
    case 405:
      message = "The HTTP method used was not allowed in this context";
      break;
    case 429:
      message = "Too Many Requests";
      break;
    case 500:
      message = "Internal Server Error";
      break;
    case 501:
      message = "Not Implemented";
      break;
    default:
      message = "An unknown error occurred";
  }

  let heading = `${errorTitle}\n${message}`;
  if (apiError) {
    heading = `${message}\n${apiError}`;
  }

  // Display the error message to the user
  notification({ heading, toastType: "error" });

  if (isLocalDev()) {
    consoleLog("DEBUG: ===========================");
    consoleLog("DEBUG: apiFetchNotOkHandler");
    consoleLog(`DEBUG: message: ${message}`);
    consoleLog(`DEBUG: apiError: ${apiError}`);
    consoleLog(`DEBUG: errorDetail: ${errorTitle}`);
    consoleLog(`DEBUG: error: ${JSON.stringify(error)}`);
    consoleLog(`DEBUG: response: ${JSON.stringify(response)}`);
  }

  const screenshotId = captureScreenshot(`Error: ${errorTitle}`);

  // Get environment for error reporting
  const environment = await getCurrentEnvironment();

  reportTelemetryError(
    error instanceof Error ? error : new Error(String(error)),
    {
      message,
      apiError,
      errorTitle,
      releaseId: process.env.BUILD_BUILDNUMBER ?? "development",
      buildVersion: process.env.BUILD_BUILDNUMBER ?? "development",
      environment,
      ...(screenshotId && { screenshotId })
    }
  );

  return error;
};

export const errorHandler = async (err: unknown, errorTitle: string) => {
  let error = err instanceof Error ? err : undefined;
  const errorResponse = err instanceof Response ? err : undefined;

  if (errorResponse) {
    apiFetchNotOkHandler(errorResponse, errorTitle);
    return;
  }

  // Fetch throws an Error object when it encounters a network error
  // The message property of this object will contain details about the error
  const heading = `${errorTitle}: ${error?.message ?? "An unknown error occurred"}`;

  // Display the error message to the user
  notification({ heading, toastType: "error" });

  if (isLocalDev()) {
    consoleLog("DEBUG: ===========================");
    consoleLog("DEBUG: errorHandler");
    consoleLog(`DEBUG: errorTitle: ${errorTitle}`);
    consoleLog(`DEBUG: error message: ${error?.message}`);
    consoleLog(`DEBUG: response: ${JSON.stringify(err)}`);
  }

  if (!error) {
    error = new Error(heading);
  }

  const screenshotId = captureScreenshot(`Error: ${errorTitle}`);

  // Get environment for error reporting
  const environment = await getCurrentEnvironment();

  reportTelemetryError(error, {
    errorTitle,
    heading,
    releaseId: process.env.BUILD_BUILDNUMBER ?? "development",
    buildVersion: process.env.BUILD_BUILDNUMBER ?? "development",
    environment,
    ...(screenshotId && { screenshotId })
  });
};
